<template>
    <div class="d-flex align-items-center justify-content-center vh-100">
        <div
            class="card p-4"
            :style="
                app_browser ? 'width: 100%' : 'width: 100%; max-width: 400px'
            "
        >
            <form
                v-if="!showSuccessView"
                class="form-horizontal"
                @submit.prevent="handleForgotPassword"
            >
                <div class="card-header text-center mb-4">
                    <img
                        :src="systemLogo"
                        alt="Logo"
                        class="img-fluid"
                        style="max-width: 150px"
                    />
                    <h4 class="mt-3">{{ systemTitle }}</h4>
                </div>
                <div class="card-body">
                    <div class="form-group mb-3">
                        <label for="email" class="form-label">{{
                            $t('login.login_msg__forgot_password')
                        }}</label>
                        <input
                            type="email"
                            v-model="email"
                            class="form-control"
                            id="email"
                            placeholder="Enter your email"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        class="btn btn-primary w-100"
                        :disabled="isLoading"
                    >
                        <span
                            v-if="isLoading"
                            class="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        {{ $t('login.login_msg__forgot_password_submit') }}
                    </button>
                </div>
            </form>
            <div v-if="successMessage" class="alert mt-3" role="alert">
                <h4 style="color: black">{{ successMessage }}</h4>
                <div
                    v-if="app_browser && showSuccessView"
                    class="mt-3 text-center"
                >
                    <button
                        type="button"
                        class="btn btn-primary w-50"
                        @click="handleSuccessOk"
                    >
                        {{ $t('users.users_msg__verification_button') || 'OK' }}
                    </button>
                </div>
            </div>
            <div
                v-if="errorMessage"
                class="alert alert-danger mt-3"
                role="alert"
            >
                {{ errorMessage }}
            </div>
            <div v-if="!app_browser" class="mt-3 text-center">
                <button class="btn btn-link p-0" @click="goToLogin">
                    <i class="fa fa-arrow-left" aria-hidden="true"></i>
                    {{ $t('login.login_msg__sign_in') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, inject, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import i18n from '@/api/i18n';
import { sendResetPasswordLink } from '@/api/users';
import apiClient from '@/api/index';

export default {
    name: 'ForgotPassword',
    setup() {
        const { t } = useI18n();
        const Swal = inject('$swal');
        const router = useRouter();
        const store = useStore();

        const email = ref('');
        const successMessage = ref('');
        const errorMessage = ref('');
        const isLoading = ref(false);
        const showSuccessView = ref(false);

        // 取得 URL 查詢參數 app_browser，例如 /forgot-password?app_browser=true
        const app_browser = computed(() => {
            return router.currentRoute.value.query.app_browser === 'true';
        });

        const handleForgotPassword = async () => {
            if (isLoading.value) return; // 防止重複提交

            isLoading.value = true;
            errorMessage.value = '';
            successMessage.value = '';

            try {
                await sendResetPasswordLink(email.value);

                // if app_browser is not true, show success alert then redirect to login page
                if (!app_browser.value) {
                    await Swal.fire({
                        title: t(
                            'users.users_title__forgot_password_email_sent'
                        ),
                        text: t('users.users_msg__forgot_password_email_sent'),
                        icon: 'success',
                        showConfirmButton: true,
                    });
                    goToLogin();
                } else {
                    // stay in the forgot password page and show success view
                    showSuccessView.value = true;
                    successMessage.value = t(
                        'users.users_msg__forgot_password_email_sent'
                    );
                }
            } catch (error) {
                // Suppress error details to prevent misuse for email verification
                // if app_browser is not true, redirect to login page
                if (!app_browser.value) {
                    await Swal.fire({
                        title: t(
                            'users.users_title__forgot_password_email_sent'
                        ),
                        text: t('users.users_msg__forgot_password_email_sent'),
                        icon: 'success',
                        showConfirmButton: true,
                    });
                    goToLogin();
                } else {
                    // stay in the forgot password page and show success view
                    showSuccessView.value = true;
                    successMessage.value = t(
                        'users.users_msg__forgot_password_email_sent'
                    );
                }
            } finally {
                isLoading.value = false;
            }
        };

        const goToLogin = () => {
            router.push('/');
        };

        const handleSuccessOk = () => {
            location.replace('/');
        };

        onMounted(async () => {
            const locale = localStorage.getItem('locale') || 'en';
            await store.dispatch('i18n/loadLocale', locale);

            const responseLang = await apiClient.get(
                `/languages/lang/${locale}?disable_cache=false`
            );
            i18n.global.setLocaleMessage(locale, responseLang.data);
            i18n.global.locale.value = locale;
        });

        const systemLogo = computed(() => {
            const logoBase64 = store.getters['system/logo'];
            if (logoBase64) {
                return `data:image/png;base64,${logoBase64}`;
            }
            return `${process.env.BASE_URL}assets/img/dhc-logo.png`;
        });

        const systemTitle = computed(() => {
            return store.getters['system/title'] || 'Club Assist SaaS';
        });

        return {
            email,
            handleForgotPassword,
            successMessage,
            errorMessage,
            goToLogin,
            systemTitle,
            systemLogo,
            app_browser,
            isLoading,
            showSuccessView,
            handleSuccessOk,
        };
    },
};
</script>
